import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSettings } from '../contexts/SettingsContext';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  Rating,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Avatar,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
  Alert,
  IconButton,
  Tooltip,
  Tab,
  Tabs
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import CategoryIcon from '@mui/icons-material/Category';
import BarChartIcon from '@mui/icons-material/BarChart';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PsychologyIcon from '@mui/icons-material/Psychology';

const TradeFeedback = () => {
  const [loading, setLoading] = useState(false);
  const [feedbacks, setFeedbacks] = useState([]);
  const [error, setError] = useState(null);
  const [ratings, setRatings] = useState({});
  const { settings, refreshSettings } = useSettings();
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState('');
  const [selectedTimeframe, setSelectedTimeframe] = useState('last10');
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [symbols, setSymbols] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [customCount, setCustomCount] = useState(20);

  useEffect(() => {
    refreshSettings();
    fetchStrategies();
    fetchSymbols();
  }, [refreshSettings]);
  
  const refreshSubscriptionData = useCallback(async () => {
    await refreshSettings();
  }, [refreshSettings]);

  const fetchStrategies = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStrategies(response.data);
    } catch (error) {
      console.error('Error fetching strategies:', error);
    }
  };

  const fetchSymbols = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/getTrades', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Extract unique symbols
      const uniqueSymbols = [...new Set(response.data.map(trade => trade.symbol))];
      setSymbols(uniqueSymbols);
    } catch (error) {
      console.error('Error fetching symbols:', error);
    }
  };

  const getFeedback = async (forceNew = false) => {
    setLoading(true);
    setError(null);
    
    // Check quota limits for new feedback generation
    if (forceNew) {
      const feedbackQuota = settings?.subscription?.aiFeedbackQuota;
      if (feedbackQuota && feedbackQuota.used >= feedbackQuota.totalLimit) {
        setError(`AI feedback limit reached (${feedbackQuota.used}/${feedbackQuota.totalLimit}). Upgrade your plan for more.`);
        setLoading(false);
        return;
      }
    }
    
    try {
      const token = localStorage.getItem('idToken');
      
      // Build filter parameters based on user selection
      const filterParams = {};
      
      if (selectedTimeframe === 'last10') {
        filterParams.limit = 10;
      } else if (selectedTimeframe === 'last20') {
        filterParams.limit = 20;
      } else if (selectedTimeframe === 'custom') {
        filterParams.limit = customCount;
      }
      
      if (selectedStrategy) {
        filterParams.strategyId = selectedStrategy;
      }
      
      if (selectedSymbol) {
        filterParams.symbol = selectedSymbol;
      }
      
      const response = await axios.post(
        'https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/tradefeedback',
        { 
          forceNew,
          incrementQuota: forceNew, // Only increment quota when generating new feedback
          filterParams // Add filter parameters to the request
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const data = response.data;
      const feedbackArray = Array.isArray(data) ? data : [data];
      setFeedbacks(feedbackArray);
      
      // Refresh settings to get updated quota information if new feedback was generated
      if (forceNew) {
        await refreshSubscriptionData();
      }
    } catch (err) {
      console.error('Error fetching feedback:', err);
      setError('Failed to fetch feedback. Try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleRating = (index, rating) => {
    setRatings(prev => ({ ...prev, [index]: rating }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getFilterDescription = () => {
    let description = '';
    
    if (selectedTimeframe === 'last10') {
      description = 'Last 10 trades';
    } else if (selectedTimeframe === 'last20') {
      description = 'Last 20 trades';
    } else if (selectedTimeframe === 'custom') {
      description = `Last ${customCount} trades`;
    }
    
    if (selectedStrategy) {
      const strategy = strategies.find(s => s.strategyId === selectedStrategy);
      description += ` for ${strategy?.name || 'selected strategy'}`;
    }
    
    if (selectedSymbol) {
      description += ` with ${selectedSymbol}`;
    }
    
    return description;
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        🧠 Trade Feedback & Coaching
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Feedback usage: {settings?.subscription?.aiFeedbackQuota?.used || 0}/{settings?.subscription?.aiFeedbackQuota?.totalLimit || 0}
        {!settings?.subscription?.aiFeedbackQuota && ' (loading...)'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Note: Maximum 20 trades can be analyzed at once due to processing limitations.
      </Typography>

      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          sx={{ mb: 3 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<HistoryIcon />} label="Recent" />
          <Tab icon={<BarChartIcon />} label="Strategy" />
          <Tab icon={<CategoryIcon />} label="Symbol" />
          <Tab icon={<PsychologyIcon />} label="Custom" />
        </Tabs>

        {activeTab === 0 && (
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Timeframe</InputLabel>
                <Select
                  value={selectedTimeframe}
                  onChange={(e) => setSelectedTimeframe(e.target.value)}
                  label="Timeframe"
                >
                  <MenuItem value="last10">Last 10 trades</MenuItem>
                  <MenuItem value="last20">Last 20 trades</MenuItem>
                  <MenuItem value="custom">Custom count</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {selectedTimeframe === 'custom' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number of trades"
                  type="number"
                  value={customCount}
                  onChange={(e) => setCustomCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 10)))}
                  InputProps={{ inputProps: { min: 1, max: 20 } }}
                  helperText="Enter a value between 1 and 20 (max limit)"
                />
              </Grid>
            )}
          </Grid>
        )}

        {activeTab === 1 && (
          <FormControl fullWidth>
            <InputLabel>Strategy</InputLabel>
            <Select
              value={selectedStrategy}
              onChange={(e) => setSelectedStrategy(e.target.value)}
              label="Strategy"
            >
              <MenuItem value="">All strategies</MenuItem>
              {strategies.map((strategy) => (
                <MenuItem key={strategy.strategyId} value={strategy.strategyId}>
                  {strategy.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {activeTab === 2 && (
          <FormControl fullWidth>
            <InputLabel>Symbol</InputLabel>
            <Select
              value={selectedSymbol}
              onChange={(e) => setSelectedSymbol(e.target.value)}
              label="Symbol"
            >
              <MenuItem value="">All symbols</MenuItem>
              {symbols.map((symbol) => (
                <MenuItem key={symbol} value={symbol}>
                  {symbol}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {activeTab === 3 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Timeframe</InputLabel>
                <Select
                  value={selectedTimeframe}
                  onChange={(e) => setSelectedTimeframe(e.target.value)}
                  label="Timeframe"
                >
                  <MenuItem value="last10">Last 10 trades</MenuItem>
                  <MenuItem value="last20">Last 20 trades</MenuItem>
                  <MenuItem value="custom">Custom count</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {selectedTimeframe === 'custom' && (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Number of trades"
                  type="number"
                  value={customCount}
                  onChange={(e) => setCustomCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 10)))}
                  InputProps={{ inputProps: { min: 1, max: 20 } }}
                  helperText="Enter a value between 1 and 20 (max limit)"
                />
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Strategy</InputLabel>
                <Select
                  value={selectedStrategy}
                  onChange={(e) => setSelectedStrategy(e.target.value)}
                  label="Strategy"
                >
                  <MenuItem value="">All strategies</MenuItem>
                  {strategies.map((strategy) => (
                    <MenuItem key={strategy.strategyId} value={strategy.strategyId}>
                      {strategy.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Symbol</InputLabel>
                <Select
                  value={selectedSymbol}
                  onChange={(e) => setSelectedSymbol(e.target.value)}
                  label="Symbol"
                >
                  <MenuItem value="">All symbols</MenuItem>
                  {symbols.map((symbol) => (
                    <MenuItem key={symbol} value={symbol}>
                      {symbol}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button 
            variant="contained" 
            onClick={() => getFeedback(false)} 
            disabled={loading}
            startIcon={<RefreshIcon />}
          >
            Load Feedback
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => getFeedback(true)} 
            disabled={loading}
            startIcon={<PsychologyIcon />}
          >
            Generate New Feedback
          </Button>
        </Box>

        {getFilterDescription() && (
          <Chip 
            label={getFilterDescription()} 
            color="primary" 
            variant="outlined" 
            sx={{ mt: 2 }} 
          />
        )}
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      <Box sx={{ mt: 2 }}>
        {feedbacks.length === 0 && !loading && !error && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No feedback available. Generate new feedback to get insights on your trading performance.
          </Alert>
        )}
        
        {feedbacks.map((entry, index) => (
          <Accordion key={index} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <PsychologyIcon />
                </Avatar>
                <Typography variant="subtitle1">
                  Feedback from {new Date(entry.timestamp).toLocaleString() || 'unknown'}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={9}>
                  <Typography sx={{ whiteSpace: 'pre-line' }}>{entry.feedback}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card elevation={2}>
                    <CardContent>
                      <Typography variant="body2" fontWeight="bold" gutterBottom>Performance Score</Typography>
                      <Rating
                        name={`performance-${index}`}
                        value={ratings[index] || 3}
                        onChange={(e, newVal) => handleRating(index, newVal)}
                        size="large"
                      />
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body2" fontWeight="bold" color="text.secondary" gutterBottom>
                        Key Insights:
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Chip 
                          icon={<TrendingUpIcon />} 
                          label="Pattern Recognition" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                        <Chip 
                          icon={<LocalOfferIcon />} 
                          label="Risk Management" 
                          size="small" 
                          color="secondary" 
                          variant="outlined"
                        />
                        <Chip 
                          icon={<BarChartIcon />} 
                          label="Performance Analysis" 
                          size="small" 
                          color="success" 
                          variant="outlined"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default TradeFeedback;